import { BedDouble, Building2, CalendarDays, CalendarRange, CreditCard, Database, Globe, LayoutGrid, ListFilter, ShieldCheck, Utensils } from "lucide-react";
import FilterBar from "../../../components/controls/FilterBar";
import MultiSelectField from "../../../components/controls/MultiSelectField";
import SelectField from "../../../components/controls/SelectField";
import AvailableDatePicker from "./AvailableDatePicker";
import { CARD_CLASS, FILTER_SELECT_CLASS } from "../styles";
import type { FilterOptions, FilterState } from "../types";

export type ParsingDateAvailability = {
  dates: string[];
  loading?: boolean;
  error?: string | null;
  restrictDate?: boolean;
  restrictDateInterval?: boolean;
};

type Props = {
  filters: FilterState;
  options: FilterOptions;
  onChange: (next: FilterState) => void;
  onApply: () => void;
  onReset: () => void;
  applyDisabled?: boolean;
  checkInRange?: { from: string; to: string } | null;
  loading?: boolean;
  showCompetitors?: boolean;
  showDateInterval?: boolean;
  showDayType?: boolean;
  dateLabel?: string;
  dateIntervalLabel?: string;
  parsingDateAvailability?: ParsingDateAvailability;
};

export default function FiltersSection({
  filters,
  options,
  onChange,
  onApply,
  onReset,
  applyDisabled = false,
  checkInRange,
  loading = false,
  showCompetitors = true,
  showDateInterval = false,
  showDayType = false,
  dateLabel,
  dateIntervalLabel,
  parsingDateAvailability,
}: Props) {
  const today = new Date();
  const todayValue = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  const maxDate = todayValue;
  const singleDateLabel = dateLabel ?? (showDateInterval ? "Дата заселения" : "Дата сбора");
  const intervalLabel = dateIntervalLabel ?? (showDateInterval ? "Период сбора данных" : "Диапазон динамики");
  const update = (key: keyof FilterState, value: string | string[]) => {
    if (key === "dateIntervalFrom" && typeof value === "string" && filters.dateIntervalTo && filters.dateIntervalTo < value) {
      onChange({ ...filters, dateIntervalFrom: value, dateIntervalTo: value });
      return;
    }
    if (key === "dateIntervalTo" && typeof value === "string" && filters.dateIntervalFrom && value && value < filters.dateIntervalFrom) {
      onChange({ ...filters, dateIntervalTo: filters.dateIntervalFrom });
      return;
    }
    onChange({ ...filters, [key]: value });
  };
  const hasActive = [filters.dataSource, filters.category, filters.ota, filters.meals, filters.refund, filters.payment, filters.room]
    .some((selected) => selected.length > 0);
  const hasDates = showDateInterval || Boolean(checkInRange?.from && checkInRange?.to);
  const hasActiveWithDates =
    hasActive ||
    Boolean(filters.dayType) ||
    Boolean(filters.date) ||
    (showCompetitors && filters.competitor.length > 0) ||
    (showDateInterval && (Boolean(filters.dateIntervalFrom) || Boolean(filters.dateIntervalTo)));
  const datesLoading = Boolean(parsingDateAvailability?.loading);
  const datesError = parsingDateAvailability?.error ?? null;
  const datePickerDisabled = loading || datesLoading || Boolean(datesError);

  return (
    <section className={`${CARD_CLASS} relative z-20`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
            <ListFilter size={18} />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Фильтры</p>
          <p className="mt-2 text-sm text-slate-500">Выберите параметры для пересчёта и сравнения цен</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onApply}
            disabled={loading || applyDisabled}
            className="px-4 py-2 rounded-2xl border border-indigo-200 bg-white text-sm font-semibold text-indigo-600 shadow-[0_10px_22px_rgba(99,102,241,0.15)] transition hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
          >
            {loading ? "Загрузка..." : "Применить фильтры"}
          </button>
          <button
            type="button"
            onClick={onReset}
            disabled={!hasActiveWithDates}
            className="px-4 py-2 rounded-2xl border bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Сбросить
          </button>
        </div>
      </div>

      <FilterBar
        onApply={undefined}
        onReset={undefined}
        hasActive={hasActiveWithDates}
        applyDisabled={loading || applyDisabled}
        sticky={false}
        className="!mt-4 !border-0 !bg-transparent !p-0 !shadow-none"
      >
        {options.dataSource.length > 0 && (
          <div className="min-w-[180px] flex-1">
            <MultiSelectField
              value={filters.dataSource}
              onChange={(value) => update("dataSource", value)}
              placeholder="Сбор данных *"
              icon={<Database size={18} />}
              options={options.dataSource}
              className={FILTER_SELECT_CLASS}
            />
          </div>
        )}
        {options.category.length > 0 && (
          <div className="min-w-[180px] flex-1">
            <MultiSelectField
              value={filters.category}
              onChange={(value) => update("category", value)}
              placeholder="Категория *"
              icon={<LayoutGrid size={18} />}
              options={options.category}
              className={FILTER_SELECT_CLASS}
            />
          </div>
        )}
        {options.ota.length > 0 && (
          <div className="min-w-[160px] flex-1">
            <MultiSelectField
              value={filters.ota}
              onChange={(value) => update("ota", value)}
              placeholder="OTA *"
              icon={<Globe size={18} />}
              options={options.ota}
              className={FILTER_SELECT_CLASS}
            />
          </div>
        )}
        {options.meals.length > 0 && (
          <div className="min-w-[170px] flex-1">
            <MultiSelectField
              value={filters.meals}
              onChange={(value) => update("meals", value)}
              placeholder="Питание *"
              icon={<Utensils size={18} />}
              options={options.meals}
              className={FILTER_SELECT_CLASS}
            />
          </div>
        )}
        {options.refund.length > 0 && (
          <div className="min-w-[200px] flex-1">
            <MultiSelectField
              value={filters.refund}
              onChange={(value) => update("refund", value)}
              placeholder="Условия возврата *"
              icon={<ShieldCheck size={18} />}
              options={options.refund}
              className={FILTER_SELECT_CLASS}
            />
          </div>
        )}
        {options.payment.length > 0 && (
          <div className="min-w-[180px] flex-1">
            <MultiSelectField
              value={filters.payment}
              onChange={(value) => update("payment", value)}
              placeholder="Тип оплаты *"
              icon={<CreditCard size={18} />}
              options={options.payment}
              className={FILTER_SELECT_CLASS}
            />
          </div>
        )}
        <div className="flex flex-wrap gap-3 basis-full">
          {showDayType && (
            <div className="min-w-[220px] flex-1">
              <SelectField
                value={filters.dayType}
                onChange={(value) => update("dayType", value)}
                placeholder="Все дни"
                icon={<CalendarDays size={18} />}
                options={[
                  { label: "Только выходные", value: "dayoff" },
                  { label: "Только будние дни", value: "workday" },
                ]}
                className={FILTER_SELECT_CLASS}
              />
              <p className="mt-2 text-xs text-slate-400">Дни недели</p>
            </div>
          )}
          {showCompetitors && options.competitors.length > 0 && (
            <div className="min-w-[220px] flex-1">
              <MultiSelectField
                value={filters.competitor}
                onChange={(value) => update("competitor", value)}
                placeholder="Конкуренты"
                icon={<Building2 size={18} />}
                options={options.competitors}
                className={FILTER_SELECT_CLASS}
              />
            </div>
          )}
          {options.room.length > 0 && (
            <div className="min-w-[180px] flex-1">
              <MultiSelectField
                value={filters.room}
                onChange={(value) => update("room", value)}
                placeholder="Комната *"
                icon={<BedDouble size={18} />}
                options={options.room}
                className={FILTER_SELECT_CLASS}
              />
            </div>
          )}
          {hasDates && (
            <div className="min-w-[220px] flex-1">
              {parsingDateAvailability?.restrictDate ? (
                <AvailableDatePicker
                  value={filters.date}
                  onChange={(value) => update("date", value)}
                  availableDates={parsingDateAvailability.dates}
                  loading={datesLoading}
                  error={datesError}
                  disabled={datePickerDisabled}
                  maxDate={showDateInterval ? undefined : maxDate}
                  label={singleDateLabel}
                  icon={<CalendarRange size={18} />}
                  variant="field"
                />
              ) : (
                <AvailableDatePicker
                  value={filters.date}
                  onChange={(value) => update("date", value)}
                  availableDates={[]}
                  disabled={loading}
                  maxDate={showDateInterval ? undefined : maxDate}
                  label={singleDateLabel}
                  icon={<CalendarRange size={18} />}
                  variant="field"
                  restrictToAvailableDates={false}
                />
              )}
              <p className="mt-2 text-xs text-slate-400">{singleDateLabel}</p>
              {parsingDateAvailability?.restrictDate && datesError && (
                <p className="mt-1 text-xs font-medium text-rose-500">{datesError}</p>
              )}
            </div>
          )}
          {showDateInterval && hasDates && (
            <div className="min-w-0 flex-[2_1_320px]">
              <div className="grid min-w-0 w-full grid-cols-1 gap-2 xl:grid-cols-2">
                  {parsingDateAvailability?.restrictDateInterval ? (
                    <>
                      <AvailableDatePicker
                        value={filters.dateIntervalFrom}
                        onChange={(value) => update("dateIntervalFrom", value)}
                        availableDates={parsingDateAvailability.dates}
                        loading={datesLoading}
                        error={datesError}
                        disabled={datePickerDisabled}
                        label={`${intervalLabel} от`}
                        icon={<CalendarRange size={18} />}
                        variant="field"
                      />
                      <AvailableDatePicker
                        value={filters.dateIntervalTo}
                        onChange={(value) => update("dateIntervalTo", value)}
                        availableDates={parsingDateAvailability.dates}
                        loading={datesLoading}
                        error={datesError}
                        disabled={datePickerDisabled}
                        minDate={filters.dateIntervalFrom || undefined}
                        label={`${intervalLabel} до`}
                        icon={<CalendarRange size={18} />}
                        variant="field"
                      />
                    </>
                  ) : (
                    <>
                      <AvailableDatePicker
                        value={filters.dateIntervalFrom}
                        onChange={(value) => update("dateIntervalFrom", value)}
                        availableDates={[]}
                        disabled={loading}
                        label={`${intervalLabel} от`}
                        icon={<CalendarRange size={18} />}
                        variant="field"
                        restrictToAvailableDates={false}
                      />
                      <AvailableDatePicker
                        value={filters.dateIntervalTo}
                        onChange={(value) => update("dateIntervalTo", value)}
                        availableDates={[]}
                        disabled={loading}
                        minDate={filters.dateIntervalFrom || undefined}
                        label={`${intervalLabel} до`}
                        icon={<CalendarRange size={18} />}
                        variant="field"
                        restrictToAvailableDates={false}
                      />
                    </>
                  )}
              </div>
              <p className="mt-2 text-xs text-slate-400">{intervalLabel} от / до</p>
              {parsingDateAvailability?.restrictDateInterval && datesError && (
                <p className="mt-1 text-xs font-medium text-rose-500">{datesError}</p>
              )}
            </div>
          )}
        </div>
      </FilterBar>
    </section>
  );
}
